import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  CheckCircle,
  ErrorOutline,
  Person,
  Security,
  SettingsSuggest,
  Timeline
} from '@mui/icons-material';
import { activityLogsAPI } from '../../services/api';

const numberFormat = (value) => Number(value || 0).toLocaleString();

const metricStyles = {
  total_logs: { color: '#1f6feb', bg: '#edf5ff' },
  issue_logs: { color: '#b42318', bg: '#fff1f1' },
  failed_logs: { color: '#c2410c', bg: '#fff7ed' },
  security_logs: { color: '#047857', bg: '#ecfdf5' },
  user_logs: { color: '#7c3aed', bg: '#f5f3ff' },
  system_logs: { color: '#475569', bg: '#f8fafc' }
};

const LogsStatistics = ({ filters }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchStatistics = async () => {
      setLoading(true);

      try {
        const response = await activityLogsAPI.getStatistics(filters);
        if (isMounted) {
          setStats(response.data.data || null);
        }
      } catch (err) {
        console.error('Error fetching activity log statistics:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStatistics();

    return () => {
      isMounted = false;
    };
  }, [filters]);

  const metrics = useMemo(() => {
    const summary = stats?.summary || {};

    return [
      {
        key: 'total_logs',
        label: 'Total Logs',
        value: summary.total_logs,
        icon: <Timeline />
      },
      {
        key: 'issue_logs',
        label: 'Error/Critical',
        value: summary.issue_logs,
        icon: <ErrorOutline />
      },
      {
        key: 'failed_logs',
        label: 'Failed',
        value: summary.failed_logs,
        icon: <ErrorOutline />
      },
      {
        key: 'security_logs',
        label: 'Security',
        value: summary.security_logs,
        icon: <Security />
      },
      {
        key: 'user_logs',
        label: 'User Actions',
        value: summary.user_logs,
        icon: <Person />
      },
      {
        key: 'system_logs',
        label: 'System Tasks',
        value: summary.system_logs,
        icon: <SettingsSuggest />
      }
    ];
  }, [stats]);

  const totalLogs = Number(stats?.summary?.total_logs || 0);
  const successLogs = Number(stats?.summary?.success_logs || 0);
  const successRate = totalLogs > 0 ? Math.round((successLogs / totalLogs) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Log Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Latest activity: {stats?.summary?.latest_activity_at || 'N/A'}
            </Typography>
          </Box>
          {loading && <CircularProgress size={22} />}
        </Stack>

        <Grid container spacing={1.5}>
          {metrics.map((metric) => {
            const style = metricStyles[metric.key];

            return (
              <Grid item xs={6} md={2} key={metric.key}>
                <Box
                  sx={{
                    height: '100%',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: '#fff'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: style.color }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 1.25,
                        bgcolor: style.bg
                      }}
                    >
                      {metric.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                        {numberFormat(metric.value)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {metric.label}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircle color="success" fontSize="small" />
                  <Typography variant="body2" fontWeight={700}>
                    Success Rate
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight={800}>
                  {successRate}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={successRate}
                sx={{ height: 8, borderRadius: 8 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#fff' }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                Top Module
              </Typography>
              <Typography variant="h6" fontWeight={800} noWrap>
                {stats?.module_stats?.[0]?.module || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {numberFormat(stats?.module_stats?.[0]?.count)} logs
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#fff' }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                Top Action
              </Typography>
              <Typography variant="h6" fontWeight={800} noWrap>
                {stats?.action_stats?.[0]?.action || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {numberFormat(stats?.action_stats?.[0]?.count)} logs
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};

export default LogsStatistics;
